import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  BackupExecutionDto,
  BackupScheduleDto,
  DatabaseBackupsDto,
  S3StorageEntry,
  SubscriptionBackupsDto,
} from '@cpm/shared';
import { BACKUP_FREQUENCY_PRESETS, BACKUP_FREQUENCY_PRESET_CRON } from '@cpm/shared';
import { Archive, BookOpen, Pencil, Play, Plus, Trash2 } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiClientError } from '../api/client.js';
import { useMe } from '../api/auth.js';
import { timeAgo } from './charts.js';
import { Modal } from './Modal.js';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  ErrorNote,
  Input,
  Label,
  Select,
  Spinner,
  type BadgeTone,
} from './ui.js';

/**
 * Backup panels (ADR-0015): schedule management + execution history on the
 * database detail page, and the per-subscription rollup. Honest by design:
 * restores are manual (in-app restore guide), S3 destinations come from the
 * admin-maintained registry, and existing schedules stay visible even when
 * the plan no longer includes the feature.
 */

/** The in-app restore procedure — linked from every backups view. */
export const RESTORE_GUIDE_PATH = '/help/backups/restore';

export function RestoreGuideLink({ children = 'restore guide' }: { children?: string }) {
  return (
    <Link
      to={RESTORE_GUIDE_PATH}
      className="inline-flex items-center gap-1 font-medium text-brand-600 hover:underline dark:text-brand-400"
    >
      <BookOpen className="size-3.5" /> {children}
    </Link>
  );
}

function formatBytes(bytes: number | null): string {
  if (bytes === null) return '—';
  if (bytes >= 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${bytes} B`;
}

export function executionTone(status: BackupExecutionDto['status']): BadgeTone {
  if (status === 'success') return 'green';
  if (status === 'failed') return 'red';
  if (status === 'running') return 'blue';
  return 'gray';
}

function frequencyLabel(frequency: string): string {
  const bare = frequency.startsWith('@') ? frequency.slice(1) : frequency;
  if ((BACKUP_FREQUENCY_PRESETS as readonly string[]).includes(bare)) {
    return bare.replace('_', ' ');
  }
  return frequency;
}

function retentionSummary(schedule: BackupScheduleDto): string {
  const parts: string[] = [];
  if (schedule.retentionAmountLocally > 0) parts.push(`keep ${schedule.retentionAmountLocally}`);
  if (schedule.retentionDaysLocally > 0) parts.push(`${schedule.retentionDaysLocally}d`);
  if (schedule.retentionMaxStorageGbLocally > 0)
    parts.push(`≤${schedule.retentionMaxStorageGbLocally} GB`);
  const local = parts.length > 0 ? `local: ${parts.join(', ')}` : 'local: unlimited';
  if (!schedule.saveS3) return local;
  const s3Parts: string[] = [];
  if (schedule.retentionAmountS3 > 0) s3Parts.push(`keep ${schedule.retentionAmountS3}`);
  if (schedule.retentionDaysS3 > 0) s3Parts.push(`${schedule.retentionDaysS3}d`);
  if (schedule.retentionMaxStorageGbS3 > 0) s3Parts.push(`≤${schedule.retentionMaxStorageGbS3} GB`);
  return `${local} · S3: ${s3Parts.length > 0 ? s3Parts.join(', ') : 'unlimited'}`;
}

// ── Shared schedule form (create/edit + plan policy) ─────────────────

export interface ScheduleFormValues {
  frequency: string;
  enabled: boolean;
  saveS3: boolean;
  s3StorageUuid: string | null;
  dumpAll: boolean;
  retentionAmountLocally: number;
  retentionDaysLocally: number;
  retentionMaxStorageGbLocally: number;
  retentionAmountS3: number;
  retentionDaysS3: number;
  retentionMaxStorageGbS3: number;
}

export const DEFAULT_SCHEDULE_VALUES: ScheduleFormValues = {
  frequency: 'daily',
  enabled: true,
  saveS3: false,
  s3StorageUuid: null,
  dumpAll: false,
  retentionAmountLocally: 7,
  retentionDaysLocally: 0,
  retentionMaxStorageGbLocally: 0,
  retentionAmountS3: 0,
  retentionDaysS3: 0,
  retentionMaxStorageGbS3: 0,
};

function Checkbox({
  id,
  checked,
  onChange,
  children,
}: {
  id: string;
  checked: boolean;
  onChange: (value: boolean) => void;
  children: ReactNode;
}) {
  return (
    <label htmlFor={id} className="flex items-center gap-2 text-sm">
      <input
        id={id}
        type="checkbox"
        className="size-4 rounded border-slate-300 text-brand-600 focus:ring-brand-600"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      {children}
    </label>
  );
}

function RetentionInput({
  id,
  label,
  value,
  onChange,
  step,
}: {
  id: string;
  label: string;
  value: number;
  onChange: (value: number) => void;
  step?: string;
}) {
  return (
    <div>
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        type="number"
        min={0}
        step={step ?? '1'}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
      />
    </div>
  );
}

/**
 * The schedule fields shared by schedule create/edit and the plan-policy
 * editor. Retention 0 = "no limit of this kind" (Coolify's default);
 * max-storage is in GB — Coolify's unit.
 */
export function ScheduleFormFields({
  values,
  onChange,
  s3Storages,
  idPrefix,
}: {
  values: ScheduleFormValues;
  onChange: (values: ScheduleFormValues) => void;
  s3Storages: S3StorageEntry[];
  idPrefix: string;
}) {
  const presetKeys = BACKUP_FREQUENCY_PRESETS as readonly string[];
  const isPreset = presetKeys.includes(values.frequency);
  const [customCron, setCustomCron] = useState(!isPreset);
  const set = (patch: Partial<ScheduleFormValues>) => onChange({ ...values, ...patch });

  return (
    <>
      <div>
        <Label htmlFor={`${idPrefix}-frequency`}>Frequency</Label>
        <Select
          id={`${idPrefix}-frequency`}
          value={customCron ? 'custom' : values.frequency}
          onChange={(e) => {
            if (e.target.value === 'custom') {
              setCustomCron(true);
              set({ frequency: isPreset ? '0 3 * * *' : values.frequency });
            } else {
              setCustomCron(false);
              set({ frequency: e.target.value });
            }
          }}
        >
          {BACKUP_FREQUENCY_PRESETS.map((preset) => (
            <option key={preset} value={preset}>
              {preset.replace('_', ' ')} ({BACKUP_FREQUENCY_PRESET_CRON[preset]})
            </option>
          ))}
          <option value="custom">custom cron expression…</option>
        </Select>
        {customCron && (
          <Input
            className="mt-2 font-mono"
            aria-label="Cron expression"
            required
            value={values.frequency}
            onChange={(e) => set({ frequency: e.target.value })}
            placeholder="0 3 * * *"
          />
        )}
      </div>

      <div className="flex flex-wrap gap-4">
        <Checkbox id={`${idPrefix}-enabled`} checked={values.enabled} onChange={(v) => set({ enabled: v })}>
          Enabled
        </Checkbox>
        <Checkbox id={`${idPrefix}-dumpall`} checked={values.dumpAll} onChange={(v) => set({ dumpAll: v })}>
          Dump all databases (PostgreSQL)
        </Checkbox>
      </div>

      <fieldset className="rounded-lg border border-slate-200 p-3 dark:border-slate-800">
        <legend className="px-1 text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
          Local retention (0 = no limit)
        </legend>
        <div className="grid grid-cols-3 gap-3">
          <RetentionInput
            id={`${idPrefix}-ral`}
            label="Keep files"
            value={values.retentionAmountLocally}
            onChange={(v) => set({ retentionAmountLocally: v })}
          />
          <RetentionInput
            id={`${idPrefix}-rdl`}
            label="Max age (days)"
            value={values.retentionDaysLocally}
            onChange={(v) => set({ retentionDaysLocally: v })}
          />
          <RetentionInput
            id={`${idPrefix}-rml`}
            label="Max total (GB)"
            step="0.1"
            value={values.retentionMaxStorageGbLocally}
            onChange={(v) => set({ retentionMaxStorageGbLocally: v })}
          />
        </div>
      </fieldset>

      <div className="space-y-3">
        <Checkbox id={`${idPrefix}-saves3`} checked={values.saveS3} onChange={(v) => set({ saveS3: v })}>
          Also upload to S3
        </Checkbox>
        {values.saveS3 && (
          <>
            {s3Storages.length === 0 ? (
              <p className="rounded-lg bg-amber-50 px-4 py-3 text-sm text-amber-700 ring-1 ring-inset ring-amber-600/10 dark:bg-amber-500/10 dark:text-amber-400">
                No S3 destinations registered. Create the S3 storage in Coolify's UI first, then
                register its uuid under{' '}
                <Link to="/backups" className="font-medium underline">
                  Backups → S3 destinations
                </Link>{' '}
                (Coolify's API cannot list S3 storages).
              </p>
            ) : (
              <div>
                <Label htmlFor={`${idPrefix}-s3`}>S3 destination</Label>
                <Select
                  id={`${idPrefix}-s3`}
                  required
                  value={values.s3StorageUuid ?? ''}
                  onChange={(e) => set({ s3StorageUuid: e.target.value || null })}
                >
                  <option value="">Select a registered S3 storage…</option>
                  {s3Storages.map((storage) => (
                    <option key={storage.uuid} value={storage.uuid}>
                      {storage.label} ({storage.uuid})
                    </option>
                  ))}
                </Select>
              </div>
            )}
            <fieldset className="rounded-lg border border-slate-200 p-3 dark:border-slate-800">
              <legend className="px-1 text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
                S3 retention (0 = no limit)
              </legend>
              <div className="grid grid-cols-3 gap-3">
                <RetentionInput
                  id={`${idPrefix}-ras`}
                  label="Keep files"
                  value={values.retentionAmountS3}
                  onChange={(v) => set({ retentionAmountS3: v })}
                />
                <RetentionInput
                  id={`${idPrefix}-rds`}
                  label="Max age (days)"
                  value={values.retentionDaysS3}
                  onChange={(v) => set({ retentionDaysS3: v })}
                />
                <RetentionInput
                  id={`${idPrefix}-rms`}
                  label="Max total (GB)"
                  step="0.1"
                  value={values.retentionMaxStorageGbS3}
                  onChange={(v) => set({ retentionMaxStorageGbS3: v })}
                />
              </div>
            </fieldset>
          </>
        )}
      </div>
    </>
  );
}

export function scheduleToFormValues(schedule: BackupScheduleDto): ScheduleFormValues {
  return {
    frequency: schedule.frequency,
    enabled: schedule.enabled,
    saveS3: schedule.saveS3,
    // Coolify reads only expose the internal integer id, never the uuid —
    // editing an S3-enabled schedule requires re-picking the destination.
    s3StorageUuid: null,
    dumpAll: schedule.dumpAll,
    retentionAmountLocally: schedule.retentionAmountLocally,
    retentionDaysLocally: schedule.retentionDaysLocally,
    retentionMaxStorageGbLocally: schedule.retentionMaxStorageGbLocally,
    retentionAmountS3: schedule.retentionAmountS3,
    retentionDaysS3: schedule.retentionDaysS3,
    retentionMaxStorageGbS3: schedule.retentionMaxStorageGbS3,
  };
}

// ── Database detail: Backups tab ─────────────────────────────────────

function ScheduleForm({
  apiBase,
  queryKey,
  s3Storages,
  existing,
  onDone,
}: {
  apiBase: string;
  queryKey: unknown[];
  s3Storages: S3StorageEntry[];
  existing?: BackupScheduleDto;
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const [values, setValues] = useState<ScheduleFormValues>(
    existing ? scheduleToFormValues(existing) : DEFAULT_SCHEDULE_VALUES,
  );
  const [backupNow, setBackupNow] = useState(false);

  const save = useMutation({
    mutationFn: () => {
      const payload: Record<string, unknown> = {
        ...values,
        s3StorageUuid: values.saveS3 ? values.s3StorageUuid : null,
      };
      // Editing an S3 schedule without re-picking a destination: leave the
      // S3 config untouched — Coolify (and the schema) require the uuid in
      // any payload that carries saveS3: true.
      if (existing?.saveS3 && values.saveS3 && values.s3StorageUuid === null) {
        delete payload.saveS3;
        delete payload.s3StorageUuid;
      }
      return existing
        ? api.patch(`${apiBase}/schedules/${existing.uuid}`, payload)
        : api.post(`${apiBase}/schedules`, { ...payload, backupNow });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey });
      onDone();
    },
  });

  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <ScheduleFormFields
        values={values}
        onChange={setValues}
        s3Storages={s3Storages}
        idPrefix="schedule"
      />
      {!existing && (
        <Checkbox id="schedule-now" checked={backupNow} onChange={setBackupNow}>
          Run a first backup immediately
        </Checkbox>
      )}
      {existing && existing.saveS3 && values.saveS3 && values.s3StorageUuid === null && (
        <p className="text-xs text-slate-500 dark:text-slate-400">
          This schedule already uploads to S3 storage #{existing.s3StorageId ?? '?'} — leaving the
          destination unselected keeps it unchanged.
        </p>
      )}
      {save.error != null && <ErrorNote error={save.error} />}
      <div className="flex justify-end">
        <Button type="submit" loading={save.isPending}>
          {existing ? 'Save changes' : 'Create schedule'}
        </Button>
      </div>
    </form>
  );
}

function ExecutionsList({ apiBase, scheduleUuid }: { apiBase: string; scheduleUuid: string }) {
  const executions = useQuery({
    queryKey: ['backups', apiBase, scheduleUuid, 'executions'],
    queryFn: () =>
      api.get<{ executions: BackupExecutionDto[] }>(
        `${apiBase}/schedules/${scheduleUuid}/executions`,
      ),
    // Running executions resolve within a poll or two.
    refetchInterval: 30_000,
  });

  if (executions.isPending) return <Spinner />;
  if (executions.error) return <ErrorNote error={executions.error} />;
  if (executions.data.executions.length === 0) {
    return (
      <p className="px-5 py-4 text-sm text-slate-500 dark:text-slate-400">
        No backups have run yet for this schedule.
      </p>
    );
  }
  return (
    <ul className="divide-y divide-slate-100 text-sm dark:divide-slate-800">
      {executions.data.executions.map((execution) => (
        <li key={execution.uuid} className="flex items-start justify-between gap-3 px-5 py-2.5">
          <div className="min-w-0">
            <p className="flex items-center gap-2">
              <Badge tone={executionTone(execution.status)}>{execution.status}</Badge>
              <span className="text-xs text-slate-500 dark:text-slate-400">
                {execution.createdAt ? timeAgo(execution.createdAt) : 'unknown time'}
              </span>
            </p>
            {execution.filename && (
              <p className="mt-1 truncate font-mono text-xs text-slate-500 dark:text-slate-400">
                {execution.filename}
              </p>
            )}
            {execution.message && (
              <p className="mt-1 text-xs text-red-600 dark:text-red-400">{execution.message}</p>
            )}
          </div>
          <span className="shrink-0 tabular-nums text-xs text-slate-500 dark:text-slate-400">
            {formatBytes(execution.sizeBytes)}
          </span>
        </li>
      ))}
    </ul>
  );
}

export function BackupsPanel({ resourceId, enabled }: { resourceId: string; enabled: boolean }) {
  const queryClient = useQueryClient();
  const me = useMe();
  const isAdmin = me.data?.role === 'admin';
  const apiBase = `/api/backups/databases/${resourceId}`;
  const queryKey = ['backups', 'database', resourceId];
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState<BackupScheduleDto | null>(null);
  const [executionsFor, setExecutionsFor] = useState<string | null>(null);

  const backups = useQuery({
    queryKey,
    queryFn: () => api.get<DatabaseBackupsDto>(apiBase),
    enabled,
    retry: false,
  });

  const runNow = useMutation({
    mutationFn: (scheduleUuid: string) =>
      api.post(`${apiBase}/schedules/${scheduleUuid}/backup-now`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['backups'] });
    },
  });
  const applyPolicy = useMutation({
    mutationFn: () => api.post(`${apiBase}/apply-policy`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey }),
  });
  const remove = useMutation({
    mutationFn: ({ scheduleUuid, deleteS3 }: { scheduleUuid: string; deleteS3: boolean }) =>
      api.delete(`${apiBase}/schedules/${scheduleUuid}?deleteS3=${deleteS3}`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey }),
  });

  if (!enabled) return null;
  if (backups.isPending) return <Spinner />;
  if (backups.error) {
    if (backups.error instanceof ApiClientError && backups.error.status === 404) {
      return (
        <Card>
          <EmptyState
            icon={<Archive className="size-10" />}
            title="Backups module is disabled"
            description="An administrator can enable it under Modules."
          />
        </Card>
      );
    }
    return <ErrorNote error={backups.error} />;
  }
  const data = backups.data;

  if (!data.engineSupported && data.schedules.length === 0) {
    return (
      <Card>
        <EmptyState
          icon={<Archive className="size-10" />}
          title="No backup support for this engine"
          description="Coolify's scheduled backups cover PostgreSQL, MySQL, MariaDB and MongoDB. Other engines cannot be dumped — plan for application-level export if this data matters."
        />
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {!data.featureEnabled && (
        <Card>
          <EmptyState
            title="Backups are not included in this plan"
            description={
              data.schedules.length > 0
                ? 'Existing schedules remain visible below and can be deleted, but not created or changed. Upgrade the plan to manage backups.'
                : 'Upgrade the plan or add a subscription override to enable scheduled backups.'
            }
          />
        </Card>
      )}

      {(data.featureEnabled || data.schedules.length > 0) && (
        <Card>
          <CardHeader
            title="Backup schedules"
            description={
              <>
                Runs and stores dumps on the database's server via Coolify. Restoring is a manual
                procedure — follow the <RestoreGuideLink />.
              </>
            }
            actions={
              data.featureEnabled ? (
                <Button onClick={() => setAddOpen(true)}>
                  <Plus className="size-4" /> Add schedule
                </Button>
              ) : undefined
            }
          />
          {data.liveError && (
            <div className="px-5 pt-3">
              <ErrorNote error={new Error(data.liveError)} />
            </div>
          )}
          {(runNow.error ?? remove.error ?? applyPolicy.error) != null && (
            <div className="px-5 pt-3">
              <ErrorNote error={runNow.error ?? remove.error ?? applyPolicy.error} />
            </div>
          )}
          {data.schedules.length === 0 && !data.liveError ? (
            <EmptyState
              icon={<Archive className="size-10" />}
              title="No backup schedules"
              description="Create a schedule, or apply the plan's default backup policy."
              action={
                data.featureEnabled ? (
                  <Button
                    variant="secondary"
                    loading={applyPolicy.isPending}
                    onClick={() => applyPolicy.mutate()}
                  >
                    Apply plan policy
                  </Button>
                ) : undefined
              }
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500 dark:border-slate-800 dark:text-slate-400">
                  <tr>
                    <th className="px-5 py-3 font-medium">Frequency</th>
                    <th className="px-5 py-3 font-medium">State</th>
                    <th className="px-5 py-3 font-medium">Destination</th>
                    <th className="px-5 py-3 font-medium">Retention</th>
                    <th className="px-5 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {data.schedules.map((schedule) => (
                    <tr key={schedule.uuid}>
                      <td className="px-5 py-3 font-mono text-xs">
                        {frequencyLabel(schedule.frequency)}
                      </td>
                      <td className="px-5 py-3">
                        <Badge tone={schedule.enabled ? 'green' : 'gray'}>
                          {schedule.enabled ? 'enabled' : 'disabled'}
                        </Badge>
                      </td>
                      <td className="px-5 py-3 text-xs">
                        local
                        {schedule.saveS3 &&
                          ` + S3${schedule.s3StorageId !== null ? ` (#${schedule.s3StorageId})` : ''}`}
                        {schedule.dumpAll && (
                          <span className="text-slate-500 dark:text-slate-400"> · dump all</span>
                        )}
                      </td>
                      <td className="px-5 py-3 text-xs text-slate-500 dark:text-slate-400">
                        {retentionSummary(schedule)}
                      </td>
                      <td className="whitespace-nowrap px-5 py-3 text-right">
                        <Button
                          variant="ghost"
                          onClick={() =>
                            setExecutionsFor(
                              executionsFor === schedule.uuid ? null : schedule.uuid,
                            )
                          }
                        >
                          {executionsFor === schedule.uuid ? 'Hide runs' : 'Runs'}
                        </Button>
                        {data.featureEnabled && (
                          <>
                            <Button
                              variant="ghost"
                              aria-label="Run backup now"
                              loading={runNow.isPending}
                              onClick={() => runNow.mutate(schedule.uuid)}
                            >
                              <Play className="size-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              aria-label="Edit schedule"
                              onClick={() => setEditing(schedule)}
                            >
                              <Pencil className="size-4" />
                            </Button>
                          </>
                        )}
                        {isAdmin && (
                          <Button
                            variant="ghost"
                            className="hover:!text-red-600"
                            aria-label="Delete schedule"
                            onClick={() => {
                              if (
                                !window.confirm(
                                  'Delete this backup schedule? Its execution history and local dump files are deleted too.',
                                )
                              ) {
                                return;
                              }
                              const deleteS3 =
                                schedule.saveS3 &&
                                window.confirm('Also delete the backup copies stored on S3?');
                              remove.mutate({ scheduleUuid: schedule.uuid, deleteS3 });
                            }}
                          >
                            <Trash2 className="size-4" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {executionsFor !== null && (
            <div className="border-t border-slate-200 dark:border-slate-800">
              <ExecutionsList apiBase={apiBase} scheduleUuid={executionsFor} />
            </div>
          )}
        </Card>
      )}

      <Modal title="Add backup schedule" open={addOpen} onClose={() => setAddOpen(false)}>
        <ScheduleForm
          apiBase={apiBase}
          queryKey={queryKey}
          s3Storages={data.s3Storages}
          onDone={() => setAddOpen(false)}
        />
      </Modal>
      <Modal title="Edit backup schedule" open={editing !== null} onClose={() => setEditing(null)}>
        {editing && (
          <ScheduleForm
            apiBase={apiBase}
            queryKey={queryKey}
            s3Storages={data.s3Storages}
            existing={editing}
            onDone={() => setEditing(null)}
          />
        )}
      </Modal>
    </div>
  );
}

// ── Subscription detail: backups rollup ──────────────────────────────

export function SubscriptionBackupsPanel({ subscriptionId }: { subscriptionId: string }) {
  const backups = useQuery({
    queryKey: ['backups', 'subscription', subscriptionId],
    queryFn: () => api.get<SubscriptionBackupsDto>(`/api/backups/subscriptions/${subscriptionId}`),
    retry: false,
    refetchInterval: 60_000,
  });

  // Module disabled or Coolify unconfigured — hide the card entirely.
  if (backups.error) return null;
  if (backups.isPending) {
    return (
      <Card>
        <CardHeader title="Backups" />
        <Spinner />
      </Card>
    );
  }
  const data = backups.data;
  if (data.databases.length === 0) return null;

  return (
    <Card>
      <CardHeader
        title="Backups"
        description={
          <>
            Scheduled database backups via Coolify · restores are manual, see the <RestoreGuideLink />
          </>
        }
        actions={
          data.featureEnabled ? undefined : <Badge tone="gray">not in plan</Badge>
        }
      />
      <ul className="divide-y divide-slate-100 text-sm dark:divide-slate-800">
        {data.databases.map((db) => {
          const lastExecution = db.executions[0] ?? null;
          const enabledCount = db.schedules.filter((s) => s.enabled).length;
          return (
            <li key={db.resourceId} className="flex items-start justify-between gap-3 px-5 py-3">
              <div className="min-w-0">
                <Link
                  to={`/databases/${db.resourceId}`}
                  className="font-medium text-brand-600 hover:underline dark:text-brand-400"
                >
                  {db.name}
                </Link>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  {db.error
                    ? db.error
                    : !db.engineSupported && db.schedules.length === 0
                      ? 'Engine has no backup support in Coolify'
                      : db.schedules.length === 0
                      ? 'No backup schedule'
                      : `${db.schedules.length} schedule${db.schedules.length === 1 ? '' : 's'}` +
                        (enabledCount < db.schedules.length
                          ? ` (${enabledCount} enabled)`
                          : '') +
                        (lastExecution?.createdAt
                          ? ` · last run ${timeAgo(lastExecution.createdAt)}`
                          : ' · never ran')}
                </p>
                {lastExecution?.status === 'failed' && lastExecution.message && (
                  <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                    {lastExecution.message}
                  </p>
                )}
              </div>
              <span className="shrink-0">
                {db.error ? (
                  <Badge tone="yellow">unavailable</Badge>
                ) : db.schedules.length === 0 ? (
                  <Badge tone={data.featureEnabled && db.engineSupported ? 'yellow' : 'gray'}>
                    {!db.engineSupported
                      ? 'not supported'
                      : data.featureEnabled
                        ? 'unprotected'
                        : 'no backups'}
                  </Badge>
                ) : lastExecution ? (
                  <Badge tone={executionTone(lastExecution.status)}>{lastExecution.status}</Badge>
                ) : (
                  <Badge tone="blue">scheduled</Badge>
                )}
              </span>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}
